import { useState, useEffect, useRef } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Tuning Fork — preset reference pitches. Same Web Audio backend as Tone
// Generator but with preset buttons.

const NOTES = [
  { name: "A", oct: 4, hz: 440 },
  { name: "A♯", oct: 4, hz: 466.16 },
  { name: "B", oct: 4, hz: 493.88 },
  { name: "C", oct: 4, hz: 261.63 },
  { name: "C♯", oct: 4, hz: 277.18 },
  { name: "D", oct: 4, hz: 293.66 },
  { name: "D♯", oct: 4, hz: 311.13 },
  { name: "E", oct: 4, hz: 329.63 },
  { name: "F", oct: 4, hz: 349.23 },
  { name: "F♯", oct: 4, hz: 369.99 },
  { name: "G", oct: 4, hz: 392.00 },
  { name: "G♯", oct: 4, hz: 415.30 },
];

const GUITAR = [
  { name: "E (low)", hz: 82.41 },
  { name: "A", hz: 110.00 },
  { name: "D", hz: 146.83 },
  { name: "G", hz: 196.00 },
  { name: "B", hz: 246.94 },
  { name: "E (high)", hz: 329.63 },
];

const BASS = [
  { name: "E", hz: 41.20 },
  { name: "A", hz: 55.00 },
  { name: "D", hz: 73.42 },
  { name: "G", hz: 98.00 },
];

const VIOLIN = [
  { name: "G", hz: 196.00 },
  { name: "D", hz: 293.66 },
  { name: "A", hz: 440.00 },
  { name: "E", hz: 659.26 },
];

const STANDARDS = [
  { name: "A4 = 440 Hz", hz: 440, desc: "Concert pitch (modern standard)" },
  { name: "A4 = 432 Hz", hz: 432, desc: "Alternative tuning some use" },
  { name: "A4 = 415 Hz", hz: 415.30, desc: "Baroque pitch" },
  { name: "Middle C", hz: 261.63, desc: "C4" },
];

const TABS = [
  { id: "standards", label: "Standards" },
  { id: "chromatic", label: "Chromatic" },
  { id: "guitar", label: "Guitar" },
  { id: "bass", label: "Bass" },
  { id: "violin", label: "Violin" },
];

export default function TuningFork() {
  const [tab, setTab] = useState("standards");
  const [playing, setPlaying] = useState(null); // hz of currently-playing note
  const ctxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);

  const play = (hz) => {
    stop();
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = hz;
      gain.gain.value = 0;
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.05);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      ctxRef.current = ctx; oscRef.current = osc; gainRef.current = gain;
      setPlaying(hz);
    } catch {}
  };
  const stop = () => {
    if (!ctxRef.current) return;
    try {
      const g = gainRef.current, ctx = ctxRef.current, osc = oscRef.current;
      g.gain.cancelScheduledValues(ctx.currentTime);
      g.gain.setValueAtTime(g.gain.value, ctx.currentTime);
      g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05);
      setTimeout(() => {
        try { osc.stop(); } catch {}
        try { ctx.close(); } catch {}
      }, 80);
    } catch {}
    setPlaying(null);
    ctxRef.current = null;
  };

  useEffect(() => () => stop(), []);

  const list = tab === "chromatic" ? NOTES
    : tab === "guitar" ? GUITAR
    : tab === "bass" ? BASS
    : tab === "violin" ? VIOLIN
    : STANDARDS;

  return (
    <ToolsShell title="Tuning Fork">
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 14, justifyContent: "center" }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); stop(); }}
            style={{ background: tab === t.id ? TOOLS_ACCENT : "#1C1E2E", color: tab === t.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: tab === "chromatic" ? "repeat(3, 1fr)" : "1fr", gap: 8 }}>
        {list.map((n) => {
          const active = playing === n.hz;
          return (
            <button
              key={n.name + n.hz}
              onClick={() => active ? stop() : play(n.hz)}
              style={{
                background: active ? TOOLS_ACCENT : "#13141F",
                color: active ? "#1A0604" : "#E8E9F3",
                border: `1px solid ${active ? TOOLS_ACCENT : "#262839"}`,
                borderRadius: 10,
                padding: tab === "chromatic" ? "16px 10px" : "14px 16px",
                fontSize: 16, fontWeight: 700,
                cursor: "pointer",
                textAlign: tab === "chromatic" ? "center" : "left",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: tab === "chromatic" ? "column" : "row",
                gap: 2,
              }}
            >
              <span>{n.name}</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: active ? "#1A0604" : "#9A9DBE", fontVariantNumeric: "tabular-nums" }}>
                {n.hz.toFixed(2)} Hz
              </span>
              {n.desc && tab === "standards" && (
                <span style={{ fontSize: 12, color: active ? "#3D1A14" : "#7C8099" }}>{n.desc}</span>
              )}
            </button>
          );
        })}
      </div>

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 18, textAlign: "center" }}>
        Reference pitches at A=440 Hz equal temperament unless specified. Pure sine waves — many physical tuning forks have richer overtones.
      </p>
    </ToolsShell>
  );
}
